import { useEffect, useRef, useState } from "react";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { apiClient, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "./attachment-state-ui";

type RecordView = publicOperatorAdmin.components["schemas"]["AlignmentUnresolvedRecord"];
type Page = { appBindingId: string; records: RecordView[]; nextAfterRecordId: string | null };
export function AlignmentUnresolvedRecords({ kintoneAppId, appBindingId }: { kintoneAppId: string; appBindingId: string }) {
  const [page, setPage] = useState<Page | null>(null), [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false), [after, setAfter] = useState<string | null>(null);
  const generation = useRef(0), running = useRef(false);
  useEffect(() => () => { generation.current++; }, []);
  const path = `/kintone-apps/${encodeURIComponent(kintoneAppId)}/automatic-attachment-alignment/unresolved-records`;
  const load = async (cursor: string | null = after) => {
    const request = ++generation.current;
    setBusy(true);
    try {
      const result = await apiClient<Page>(`${path}${cursor ? `?afterRecordId=${encodeURIComponent(cursor)}` : ""}`);
      if (request !== generation.current) return;
      if (result.appBindingId !== appBindingId) throw Error("対象アプリの未解決状態を確認できません。");
      setPage(result); setAfter(cursor); setError(null);
    } catch (failure) { if (request === generation.current) setError(apiErrorMessage(failure, "未解決レコードを取得できませんでした。")); }
    finally { if (request === generation.current) setBusy(false); }
  };
  const reregister = async (record: RecordView) => {
    if (running.current) return;
    running.current = true; setBusy(true);
    const request = ++generation.current;
    try {
      await apiClient<RecordView>(`${path}/${encodeURIComponent(record.recordId)}/reregister`, {
        method: "POST", body: JSON.stringify({ expectedRevision: record.revision }),
      });
      if (request === generation.current) await load();
    } catch (failure) {
      if (request === generation.current) {
        await load();
        setError(apiErrorMessage(failure, "再登録結果を確認できません。一覧の現在状態を確認してください。"));
      }
    } finally { running.current = false; if (request <= generation.current) setBusy(false); }
  };
  return <section className="space-y-2" aria-label="個別未解決レコード">
    <p>未解決レコードは修復後に再試行対象へ戻せます。ジョブの再開とは別の操作です。再登録だけでは転送や自動整合化の有効化を行いません。</p>
    <Button type="button" variant="outline" disabled={busy} onClick={() => void load(null)}>未解決レコードを確認</Button>
    {error ? <p role="alert">{error}</p> : null}
    {page ? <>
      {!page.records.length ? <p>このページに未解決レコードはありません。</p> : <ul className="space-y-3">{page.records.map(record => <li key={record.recordId}>
        <p>レコード {record.recordId}：{record.reasonCode}（{record.phase}）</p>
        <p>再試行失敗 {record.retryCount}回 / 最終試行 {formatDateTime(record.lastAttemptAt)}{record.retryCount === 5 ? " / 上限到達・修復が必要です" : ""}</p>
        <Button type="button" variant="outline" disabled={busy} onClick={() => void reregister(record)}>レコード {record.recordId}を修復後に再試行対象へ戻す</Button>
      </li>)}</ul>}
      {page.nextAfterRecordId ? <Button type="button" variant="outline" disabled={busy} onClick={() => void load(page.nextAfterRecordId)}>次の50件</Button> : null}
    </> : null}
  </section>;
}
